import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import NoteAddSharpIcon from '@mui/icons-material/NoteAddSharp';
import MarkEmailReadSharpIcon from '@mui/icons-material/MarkEmailReadSharp';
import HideSourceSharpIcon from '@mui/icons-material/HideSourceSharp';
import ThumbUpAltSharpIcon from '@mui/icons-material/ThumbUpAltSharp';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';
import { FormattedMessage } from 'react-intl';
import { isEmpty } from 'lodash';
import { useNotesFilterContext } from './NotesFilterContextProvider';
import NoteEmptyPageMessage from './NoteEmptyPageMessage';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.timesheetLabel.customLabelChipColor,
    fontSize: theme.spacing(22),
    opacity: '50%',
    height: theme.spacing(18.5),
    width: theme.spacing(18.5),
    maxWidth: '350px'
  },
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  box: {
    display: 'flex',
    justifyContent: 'center'
  },
  allIcon: {
    color: theme.palette.notesLabel.all,
    fontSize: theme.spacing(22),
    transform: 'scale(1.8, 1.5)',
    marginLeft: theme.spacing(6)
  },
  inboxIcon: {
    color: theme.palette.notesLabel.inbox,
    fontSize: theme.spacing(22),
    opacity: '100%'
  },
  privateIcon: {
    color: theme.palette.notesLabel.private,
    fontSize: theme.spacing(22),
    opacity: '100%'
  },
  acceptedIcon: {
    color: theme.palette.notesLabel.accepted,
    fontSize: theme.spacing(22),
    opacity: '100%'
  },
  trashIcon: {
    color: theme.palette.notesLabel.trash,
    fontSize: theme.spacing(22),
    opacity: '100%'
  },
  title: {
    margin: theme.spacing(5, 0, 0, 0),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary
  },
  subtitle: {
    color: theme.palette.text.primary
  }
}));

export const NoteFoldersEmptyPage: React.FC = () => {
  const {
    filterContext: {
      selectedClassification,
      searchCriteria: { keywords, criterions }
    }
  } = useNotesFilterContext();
  const classes = useStyles();
  const icon = useMemo(() => {
    switch (selectedClassification) {
      case 'All':
        return <NoteAddSharpIcon className={classes.icon} />;
      case 'Unclassified':
        return <MarkEmailReadSharpIcon className={classes.icon} />;
      case 'Accepted':
        return <ThumbUpAltSharpIcon className={classes.icon} />;
      case 'Private':
        return <HideSourceSharpIcon className={classes.icon} />;
      case 'Trash':
        return <DeleteOutlineSharpIcon className={classes.icon} />;
    }
  }, [selectedClassification, classes]);

  const rebrandingTitle = useMemo(() => {
    return `note.empty.${selectedClassification.toLowerCase()}.rebrandingTitle`;
  }, [selectedClassification]);

  const rebrandingSubtitle = useMemo(() => {
    return `note.empty.${selectedClassification.toLowerCase()}.rebrandingSubtitle`;
  }, [selectedClassification]);
  if (
    (keywords && keywords?.length > 0) ||
    (!isEmpty(criterions) &&
      Object.values(criterions).filter((values) => values.length > 0).length >
        0)
  ) {
    return <NoteEmptyPageMessage />;
  }

  return (
    <div
      className={classes.wrapper}
      data-testid={`empty-note-list_${selectedClassification.toLowerCase()}`}
    >
      <Box className={classes.box}>{icon}</Box>
      <Box className={classes.box}>
        <Typography variant="h2" className={classes.title}>
          <FormattedMessage id={rebrandingTitle} />
        </Typography>
      </Box>
      <Box className={classes.box}>
        <Typography variant={'h4'} className={classes.subtitle}>
          {selectedClassification !== 'Trash' && (
            <FormattedMessage id={rebrandingSubtitle} />
          )}
        </Typography>
      </Box>
    </div>
  );
};
